import React, { Fragment, useEffect, useState } from "react";
import "./CustomEmissionList.scss";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Checkbox,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Collapse,
} from "@mui/material";
import { getCustomEmissionList } from "../../../services/emissionService";
import { checkPermissions, toastMessage } from "../../../helper";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EmptyList from "../../common/EmptyList";
import { MODE } from "../../../constants/enums";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import CustomEmissionDetailedView from "../CustomEmissionDetailedView/CustomEmissionDetailedView";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import SkeletonLoader from "../../common/Skeletons/TableSkeleton/TableSkeletonLoader";

const CustomEmissionList = () => {
  const { t, i18n } = useTranslation();
  const permissions = useSelector((state: any) => state.auth.userPermissions);
  const [customList, setCustomList] = useState([] as any);
  const [mode, setMode] = useState(MODE.PLAIN);
  const [selectedCount, setSelectedCount] = useState(0);
  const [customCount, setCustomCount] = useState(0);
  const [controller, setController] = useState({
    page: 0,
    limit: 10,
  });
  const offset = controller.page * 10;
  const [selectedSubTypesList, setSelectedSubTypesList] = useState([] as any);
  const [detailedSelectIndex, setDetailedSelectIndex] = useState(0);
  const [detailedSelectItem, setDetailedSelectItem] = useState({} as any);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [typeInfo, setTypeInfo] = useState({
    typeName: "",
    methodName: "",
  });
  let cellColumns = ["Name", "Incharge", "Facility Type", "Status", "Unit ID"];
  const navigate = useNavigate();
  const addNewUnit = () => {
    navigate("/datasource/customemission");
  };
  const onChangeSelected = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    console.log(e);
    let bunitList: any = customList;
    bunitList[index]["selected"] = e.target.checked;
    setSelectedCount(
      bunitList.filter((eachUnit: any) => eachUnit.selected)?.length || 0
    );
    setCustomList([...bunitList]);
    e.stopPropagation();
  };

  useEffect(() => {
    const reqObj: any = {
      controller: {
        ...controller,
        limit: 100,
      },
    };
    getCustomEmissionList(reqObj).then((res) => {
      const data = res.data;
      const newData = data.map((each: any) => {
        return {
          ...each,
          selected: false,
        };
      });
      setCustomList([...newData]);
      setDetailedSelectItem(() => data[detailedSelectIndex]);
      setIsLoading(false);
    });
  }, [searchText]);

  useEffect(() => {
    const offset = controller.page * 10;
    const selectedList = detailedSelectItem && detailedSelectItem.data;
    setSelectedSubTypesList(selectedList);
    if (detailedSelectItem?.data) {
      setCustomCount(detailedSelectItem?.data[0]?.subtypes?.length);
    }
  }, [detailedSelectItem, controller]);

  const handlePageChange = (event: any, newPage: number) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (event: any) => {
    setController({
      ...controller,
      limit: parseInt(event.target.value, 10),
      page: 0,
    });
  };
  const onSelectBunit = (
    e: any,
    index: number,
    typeName: string,
    methodName: string
  ) => {
    setMode(MODE.SELECTED);
    setDetailedSelectIndex(index);
    setDetailedSelectItem(customList[index]);
    setTypeInfo({ ...typeInfo, typeName: typeName, methodName: methodName });
  };
  const onClose = () => {
    setMode(MODE.PLAIN);
  };
  const handleCancel = () => {
    let bunitList: any = customList;
    bunitList.forEach((element: any) => {
      element["selected"] = false;
    });
    setCustomList([...bunitList]);
    setSelectedCount(0);
  };
  const handleDiscard = (disable?: any) => {
    let bunitList: any = customList
      .filter((eachUnit: any) => eachUnit.selected)
      .map((Unit: any) => Unit.id.toString());
    const bunitStr = bunitList.reduce(
      (prevVal: string, currVal: string, idx: number) => {
        return idx === 0 ? currVal : prevVal + ", " + currVal;
      },
      ""
    );

    handleCancel();
  };
  const navigateNext = (increment: number, pageIncrement: number) => {
    if (pageIncrement) {
      setController((prevState) => {
        return { ...prevState, page: prevState.page + pageIncrement };
      });
      setDetailedSelectIndex(() =>
        pageIncrement === -1 ? controller.limit - 1 : 0
      );
    } else if (increment) {
      setDetailedSelectItem(() => customList[detailedSelectIndex + increment]);
      setDetailedSelectIndex((prevIndex) => prevIndex + increment);
    }
  };
  const handleCollapse = (obj: any, index: number) => {
    const newList = customList.map((each: any, id: number) => {
      if (id === index) {
        return {
          ...each,
          selected: !each.selected,
        };
      }
      return each;
    });
    setCustomList(newList);
    const targetElementRef = document.getElementById(`row-${index}`);
    if (targetElementRef && index) {
      setTimeout(() => {
        targetElementRef.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  return (
    <Fragment>
      {!customList?.length && searchText === "" && !isLoading && (
        <div className="pw-empty-page flex flex-col h-100v w-full justify-center items-center gap-5">
          <img className="pw-no-items" src="assets/images/empty.png" alt=" " />
          <div>{t("Welcome to Data Source")}</div>
          <div className="pw-bunit-desc">
            Organize and manage your company's carbon emissions by creating your
            first data source
          </div>
          {checkPermissions(["CREATE_EMISSION_FACTOR"], permissions) ? (
            <div className="pw-add-new-bunit e2e-addbtn" onClick={addNewUnit}>
              <AddBoxIcon className="pw-box-icon" /> <span>{t("New")}</span>
            </div>
          ) : (
            ""
          )}
        </div>
      )}

      {(customList?.length || searchText !== "" || isLoading) &&
      mode === MODE.PLAIN ? (
        <div className="pw-blist-main overflow-y-auto h-full w-full">
          <div className="pw-blist-header w-full flex flex-row justify-end">
            {/* <OutlinedInput
              id="outlined-adornment-password "
              className="pw-search-field"
              placeholder={t("Search")}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  onClickSearch();
                }
              }}
              endAdornment={
                <InputAdornment position="end">
                  {searchText === "" ? (
                    <IconButton onClick={onClickSearch} edge="end">
                      <SearchIcon />
                    </IconButton>
                  ) : (
                    <IconButton onClick={onClickCancel} edge="end">
                      <CloseIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              }
            /> */}
            {selectedCount === 0 ? (
              <>
                {checkPermissions(["CREATE_EMISSION_FACTOR"], permissions) ? (
                  <div
                    className="pw-add-new-bunit e2e-addbtn"
                    onClick={addNewUnit}
                  >
                    <AddBoxIcon className="pw-box-icon" />{" "}
                    <span>{t("New")}</span>
                  </div>
                ) : (
                  ""
                )}
              </>
            ) : (
              <div className="flex flex-row justify-end gap-4 text-sm">
                <div
                  className="pw-bunit-button-light pw-bunit-button-next bg-white "
                  onClick={handleCancel}
                >
                  {t("Cancel")}
                </div>
                <div
                  className="pw-bunit-button-light pw-bunit-button-next bg-white pw-text-color px-2"
                  onClick={() => handleDiscard(true)}
                >
                  {t("Mark as Inactive")}
                </div>
                <div
                  className="pw-bunit-button-light pw-bunit-button-next bg-white pw-text-color px-2"
                  onClick={() => handleDiscard()}
                >
                  {t("Mark as Active")}
                </div>
                {selectedCount === 1 ? (
                  <Fragment>
                    <div
                      className="pw-bunit-button-light pw-bunit-button-next bg-white"
                      onClick={() =>
                        navigate("/organisation/facilityconfigurations/", {
                          state: {
                            id: customList?.find(
                              (eachUnit: any) => eachUnit.selected
                            )?.id,
                            isClone: true,
                          },
                        })
                      }
                    >
                      {t("Clone")}
                    </div>
                    <div
                      className="pw-bunit-button-light pw-bunit-button-next bg-white"
                      onClick={() =>
                        navigate(
                          "/organisation/facilityconfigurations/" +
                            customList?.find(
                              (eachUnit: any) => eachUnit.selected
                            )?.id,
                          {
                            state: {
                              id: customList?.find(
                                (eachUnit: any) => eachUnit.selected
                              )?.id,
                              isEdit: true,
                            },
                          }
                        )
                      }
                    >
                      {t("Edit")}
                    </div>
                  </Fragment>
                ) : (
                  ""
                )}
              </div>
            )}
          </div>
          {isLoading ? (
            <SkeletonLoader type="text" cells={cellColumns} />
          ) : (
            <Fragment>
              {customList?.length ? (
                <div className="h-70v pw-hide-scroll overflow-y-auto">
                  {customList.map((eachList: any, mainIndex: number) => {
                    return (
                      <table className="w-full border-collapse">
                        <thead className="pw-custom-head">
                          <tr id={`row-${mainIndex}`}>
                            <th
                              className="text-sm text-neutral-600 border border-neutral-200 p-2 items-center w-full flex justify-between pw-cursor-pointer"
                              colSpan={2}
                              onClick={() =>
                                handleCollapse(eachList, mainIndex)
                              }
                            >
                              {eachList.category_code} {eachList.category_name}
                              <IconButton
                                className="pw-collapse-icon"
                                aria-label="expand row"
                                size="small"
                                onClick={() =>
                                  handleCollapse(eachList, mainIndex)
                                }
                              >
                                {eachList.selected ? (
                                  <ArrowDropUpIcon />
                                ) : (
                                  <ArrowDropDownIcon />
                                )}
                              </IconButton>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <Collapse
                            in={eachList.selected}
                            timeout="auto"
                            unmountOnExit
                          >
                            {eachList.data.map(
                              (eachData: any, index: number) => {
                                return eachData.subtypes
                                  .sort((a: any, b: any) => {
                                    return a.name.localeCompare(b.name);
                                  })
                                  .map((each: any) => {
                                    return (
                                      <tr key={eachData.id}>
                                        <td
                                          className="border border-neutral-200 p-3 pw-e2e-categorytype bg-white"
                                          style={{ width: "50%" }}
                                        >
                                          {each.name}
                                        </td>
                                        <td className="border border-neutral-200 pw-e2e-categorymethod bg-white w-50 ">
                                          {each.methods
                                            .sort((a: any, b: any) => {
                                              return a.name.localeCompare(
                                                b.name
                                              );
                                            })
                                            .map(
                                              (
                                                eachMethod: any,
                                                subId: number
                                              ) => {
                                                return (
                                                  <tr
                                                    className="pw-cursor-pointer"
                                                    style={{
                                                      //borderBottom: "1px solid #EAEEF4",
                                                      height: "3rem",
                                                    }}
                                                    key={eachMethod.id}
                                                    onClick={(e) =>
                                                      onSelectBunit(
                                                        e,
                                                        mainIndex,
                                                        each.id,
                                                        eachMethod.id
                                                      )
                                                    }
                                                  >
                                                    <td
                                                      style={{
                                                        width: "40rem",
                                                        borderBottom:
                                                          subId ===
                                                          each.methods.length -
                                                            1
                                                            ? "none"
                                                            : "1px solid #EAEEF4",
                                                      }}
                                                      className="p-2"
                                                    >
                                                      <ol className="list-disc ml-4 ">
                                                        <li>
                                                          {eachMethod.name}
                                                        </li>
                                                      </ol>
                                                    </td>
                                                  </tr>
                                                );
                                              }
                                            )}
                                        </td>
                                      </tr>
                                    );
                                  });
                              }
                            )}
                          </Collapse>
                          {/* )} */}
                        </tbody>
                      </table>
                    );
                  })}
                </div>
              ) : (
                <EmptyList cellColumns={cellColumns} />
              )}
            </Fragment>
          )}
        </div>
      ) : (
        ""
      )}
      {(customList?.length || searchText !== "") && mode === MODE.SELECTED ? (
        <div className="pw-blist-main-selected h-90v flex flex-row">
          <div className="flex flex-col w-1/3 bg-neutral-200">
            <div className="h-full pw-select-side-list overflow-y-auto flex flex-col w-45 gap-px">
              <div className="flex flex-row justify-end pw-padding-side-2 gap-4">
                {/* <OutlinedInput
                  id="outlined-adornment-password "
                  className="pw-search-field pw-search-field-2"
                  placeholder={t("Search")}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      onClickSearch();
                    }
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      {searchText === "" ? (
                        <IconButton onClick={onClickSearch} edge="end">
                          <SearchIcon />
                        </IconButton>
                      ) : (
                        <IconButton onClick={onClickCancel} edge="end">
                          <CloseIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  }
                /> */}

                {checkPermissions(["CREATE_EMISSION_FACTOR"], permissions) ? (
                  <div className="pw-add-new-bunit" onClick={addNewUnit}>
                    <AddBoxIcon className="pw-box-icon" />{" "}
                    <span>{t("New")}</span>
                  </div>
                ) : (
                  ""
                )}
              </div>

              {
                <div key={detailedSelectItem.id}>
                  <div className="flex flex-col">
                    <div className="flex flex-col gap-0.5 pw-bunit-name font-medium text-sm mb-4">
                      {selectedSubTypesList.map((each: any) => {
                        const customSubTypeList = each.subtypes.slice(
                          offset,
                          offset + 10
                        );
                        return customSubTypeList.map((eachtype: any) => {
                          return (
                            <>
                              <div
                                key={eachtype.name}
                                className="p-3 bg-white gap-3 font-bold"
                              >
                                {eachtype.name}
                              </div>
                              {eachtype.methods.map(
                                (eachMethod: any, indexMethod: any) => (
                                  <div
                                    key={eachMethod.name}
                                    className={`${
                                      typeInfo.methodName === eachMethod.id &&
                                      typeInfo.typeName === eachtype.id
                                        ? "bg-neutral-200"
                                        : "bg-white"
                                    } flex flex-col justify-center p-3 pl-6 pw-cursor-pointer`}
                                    onClick={(e) =>
                                      onSelectBunit(
                                        e,
                                        detailedSelectIndex,
                                        eachtype.id,
                                        eachMethod.id
                                      )
                                    }
                                  >
                                    <p>{eachMethod.name}</p>
                                  </div>
                                )
                              )}
                            </>
                          );
                        });
                      })}
                    </div>
                  </div>
                </div>
              }
            </div>
            {selectedSubTypesList[0]?.subtypes?.length >= 10 ? (
              <TablePagination
                component="div"
                count={customCount}
                page={controller.page}
                colSpan={5}
                onPageChange={handlePageChange}
                rowsPerPage={controller.limit}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage=""
                className="flex flex-row justify-end pw-pagination"
              />
            ) : (
              ""
            )}
          </div>
          <CustomEmissionDetailedView
            bunitData={detailedSelectItem}
            currentIndex={detailedSelectIndex}
            typeInfo={typeInfo}
            controller={controller}
            navigateNext={navigateNext}
            setController={setController}
            onClose={onClose}
          />
        </div>
      ) : (
        ""
      )}
    </Fragment>
  );
};

export default CustomEmissionList;
